/**
 * 교수의 노트 모달
 * 학생 전용 - 공유 폴더(교수 노트) 표시 on/off 토글
 */
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { sharedFolderService, SharedFolderData } from '../../services/sharedFolderService';
import { getSharedFolderAnnotationVisibility, setSharedFolderAnnotationVisibility } from '../../utils/sharedFolderAnnotationStorage';
import Modal from './Modal';

const Description = styled.p`
  margin: 0 0 ${props => props.theme.spacing.lg};
  font-size: ${props => props.theme.fontSize.sm};
  color: var(--text-secondary);
  line-height: 1.5;
`;

const FolderList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xl};
  overflow: hidden;
`;

const FolderItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${props => props.theme.spacing.md};
  padding-bottom: ${props => props.theme.spacing.xl};
  border-bottom: 1px solid ${props => props.theme.colors.border.ce};
`;

const FolderContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const FolderTitle = styled.div`
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.normal};
  color: ${props => props.theme.colors.book.color6b};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ToggleButton = styled.button<{ $isVisible: boolean }>`
  position: relative;
  height: 20px;
  width: 36px;
  flex-shrink: 0;
  border-radius: ${props => props.theme.borderRadius.full};
  overflow: hidden;
  border: none;
  cursor: pointer;
  padding: 0;
  background: transparent;
  outline: none;
`;

const ToggleBackground = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  inset: 0;
  border-radius: ${props => props.theme.borderRadius.full};
  transition: background 0.3s ease;
  background: ${props => (props.$isVisible ? props.theme.colors.book.color6b : props.theme.colors.border.ee)};
`;

const ToggleSlider = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  top: 2px;
  height: 16px;
  width: 16px;
  border-radius: ${props => props.theme.borderRadius.full};
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  left: ${props => props.$isVisible ? '18px' : '2px'};
`;

const RecordsLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: ${props => props.theme.fontSize.xs};
  font-weight: ${props => props.theme.fontWeight.light};
  color: var(--primary-color, #3864C4);
  text-decoration: underline;
  cursor: pointer;
  text-align: left;
`;

const UpdateBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: ${props => props.theme.borderRadius.sm};
  background-color: #ef4444;
  color: white;
  font-size: ${props => props.theme.fontSize.xs};
  font-weight: ${props => props.theme.fontWeight.semibold};
  flex-shrink: 0;
`;

const EmptyMessage = styled.p`
  margin: 0;
  font-size: ${props => props.theme.fontSize.sm};
  color: var(--text-secondary);
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
`;

const LoadingMessage = styled.p`
  margin: 0;
  font-size: ${props => props.theme.fontSize.sm};
  color: var(--text-secondary);
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
`;

const BodyInner = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const ContentArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const ModalFooter = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-top: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.md};
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const PaginationButton = styled.button`
  background: none;
  border: none;
  font-size: ${props => props.theme.fontSize.sm};
  color: #9ca3af;
  cursor: pointer;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  &:hover:not(:disabled) {
    color: var(--text-primary);
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const PaginationText = styled.span`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.book.color6b};
`;

const PaginationCurrent = styled.span`
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.book.color4b};
`;

interface ProfessorNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookUid: string;
}

const ITEMS_PER_PAGE = 5;

const ProfessorNotesModal: React.FC<ProfessorNotesModalProps> = ({
  isOpen,
  onClose,
  bookUid,
}) => {
  const [folders, setFolders] = useState<SharedFolderData[]>([]);
  const [loading, setLoading] = useState(false);
  const [itemCounts, setItemCounts] = useState<Record<string, number>>({});
  const [visibilityMap, setVisibilityMap] = useState<Record<string, boolean>>({});
  const [currentPage, setCurrentPage] = useState(1);

  const loadFolders = useCallback(async () => {
    if (!bookUid || !isOpen) return;
    setLoading(true);
    try {
      const list = await sharedFolderService.getSharedFolders(bookUid);
      setFolders(list);
      const vis: Record<string, boolean> = {};
      list.forEach((f) => {
        vis[f.folder_uuid] = getSharedFolderAnnotationVisibility(f.folder_uuid);
      });
      setVisibilityMap(vis);
      // 개수: getFolderItems(folder_uuid, limit=1, offset=0) 호출 시 res.total로 전체 아이템 수 반환
      const counts: Record<string, number> = {};
      await Promise.all(
        list.map(async (f) => {
          try {
            const res = await sharedFolderService.getFolderItems(f.folder_uuid, 1, 0);
            counts[f.folder_uuid] = res.total;
          } catch {
            counts[f.folder_uuid] = 0;
          }
        })
      );
      setItemCounts(counts);
    } catch (err) {
      // console.error('[ProfessorNotesModal] 폴더 로드 실패:', err);
      setFolders([]);
    } finally {
      setLoading(false);
    }
  }, [bookUid, isOpen]);

  useEffect(() => {
    if (isOpen && bookUid) {
      loadFolders();
      setCurrentPage(1);
    }
  }, [isOpen, bookUid, loadFolders]);

  const handleToggle = (folderUuid: string, currentVisible: boolean) => {
    const newVisible = !currentVisible;
    setSharedFolderAnnotationVisibility(folderUuid, newVisible);
    setVisibilityMap((prev) => ({ ...prev, [folderUuid]: newVisible }));
    window.dispatchEvent(
      new CustomEvent('sharedFolderVisibilityChanged', {
        detail: { folderUuid, isVisible: newVisible },
      })
    );
  };

  const handleOpenFolderDetail = (folder: SharedFolderData) => {
    window.dispatchEvent(new CustomEvent('professorNotesOpenLearningRecords'));
    onClose();
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent('professorNotesOpenFolderDetail', { detail: { folder } })
      );
    }, 300);
  };

  const truncateTitle = (text: string, maxLen: number = 25) =>
    text.length > maxLen ? `${text.slice(0, maxLen)}...` : text;

  const totalPages = Math.max(1, Math.ceil(folders.length / ITEMS_PER_PAGE));
  const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedFolders = folders.slice(startIdx, startIdx + ITEMS_PER_PAGE);

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="교수의 노트" 
      subtitle="학습 기록의 교수노트를 켜고 끌 수 있습니다." 
      width={420} 
      height={475} 
      position={{ top: '80px', left: '50%'}} 
      containerStyle={{ transform: 'translateX(-50%)' }} 
      showCloseButton 
      showOverlay={false}
    >
      <BodyInner>
        <ContentArea>
          {loading ? (
            <LoadingMessage>로딩 중...</LoadingMessage>
          ) : folders.length === 0 ? (
            <EmptyMessage>공유받은 교수 노트가 없습니다.</EmptyMessage>
          ) : (
            <FolderList>
              {paginatedFolders.map((folder) => {
                const isVisible = visibilityMap[folder.folder_uuid] ?? getSharedFolderAnnotationVisibility(folder.folder_uuid);
                const count = itemCounts[folder.folder_uuid] ?? 0;
                return (
                  <FolderItem key={folder.folder_uuid}>
                    <FolderContent>
                      <FolderTitle title={folder.title}>
                        교수노트:{truncateTitle(folder.title)}
                      </FolderTitle>
                      {count > 0 && (
                        <RecordsLink onClick={() => handleOpenFolderDetail(folder)}>
                          {count}개의 학습 기록
                        </RecordsLink>
                      )}
                    </FolderContent>
                    {/*folder.is_new && <UpdateBadge>UPDATE</UpdateBadge>*/}
                    <ToggleButton
                      $isVisible={isVisible}
                      onClick={() => handleToggle(folder.folder_uuid, isVisible)}
                      aria-label={isVisible ? '어노테이션 숨기기' : '어노테이션 표시'}
                    >
                      <ToggleBackground $isVisible={isVisible} />
                      <ToggleSlider $isVisible={isVisible} />
                    </ToggleButton>
                  </FolderItem>
                );
              })}
            </FolderList>
          )}
        </ContentArea>
        <ModalFooter>
          <PaginationWrapper>
            <PaginationButton
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              aria-label="이전 페이지"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path fillRule="evenodd" clipRule="evenodd" d="M10.325 2.95337C10.3749 2.99612 10.4158 3.04828 10.4455 3.10688C10.4753 3.16547 10.4931 3.22934 10.4981 3.29484C10.5032 3.36035 10.4952 3.4262 10.4748 3.48863C10.4543 3.55106 10.4218 3.60886 10.379 3.6587L6.65832 8.00004L10.379 12.3414C10.4243 12.3908 10.4591 12.4488 10.4815 12.512C10.5039 12.5752 10.5134 12.6422 10.5093 12.7091C10.5053 12.776 10.4879 12.8415 10.458 12.9015C10.4282 12.9615 10.3866 13.0149 10.3357 13.0586C10.2849 13.1022 10.2257 13.1352 10.1619 13.1555C10.098 13.1758 10.0307 13.1831 9.96393 13.1769C9.89719 13.1707 9.83238 13.1511 9.77335 13.1194C9.71432 13.0876 9.66228 13.0443 9.62032 12.992L5.62032 8.32537C5.54268 8.23476 5.5 8.11936 5.5 8.00004C5.5 7.88071 5.54268 7.76532 5.62032 7.6747L9.62032 3.00804C9.70664 2.90749 9.82935 2.84533 9.96148 2.8352C10.0936 2.82508 10.2244 2.86782 10.325 2.95404" fill="#9B9B9B"/>
              </svg>
            </PaginationButton>
            <PaginationText>
              <PaginationCurrent>{currentPage}</PaginationCurrent>
              {' / '}
              {totalPages}
            </PaginationText>
            <PaginationButton
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              aria-label="다음 페이지"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path fillRule="evenodd" clipRule="evenodd" d="M5.67458 2.95326C5.7753 2.86713 5.90609 2.8245 6.03823 2.83475C6.17036 2.845 6.29302 2.90729 6.37924 3.00793L10.3792 7.6746C10.4569 7.76521 10.4996 7.8806 10.4996 7.99993C10.4996 8.11926 10.4569 8.23465 10.3792 8.32526L6.37924 12.9919C6.29159 13.0877 6.17015 13.1455 6.04059 13.1533C5.91103 13.1612 5.78351 13.1183 5.68499 13.0338C5.58647 12.9493 5.52467 12.8298 5.51265 12.7006C5.50063 12.5713 5.53933 12.4425 5.62058 12.3413L9.34124 7.99993L5.62058 3.6586C5.53428 3.55805 5.49143 3.42735 5.50143 3.29523C5.51143 3.1631 5.57346 3.04035 5.67391 2.95393" fill="#9B9B9B"/>
              </svg>
            </PaginationButton>
          </PaginationWrapper>
        </ModalFooter>
      </BodyInner>
    </Modal>
  );
};

export default ProfessorNotesModal;
